import { v4 as uuid } from 'uuid';

import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';

import { uniqueBy } from '~/utils/arrayUtils';
import { floatify } from '~/utils/mathUtils';

import type {
  CategoryRoom,
  CreatedAsset,
  NetRateFormulaState,
  OptionState,
  PackageOptionId,
  RoomAssetLink,
  RoomPricing,
} from './types';

type ProrateInput = {
  isAsiaOnly: boolean;
  lengthsOfStay: Array<number>;
};

type SetSellPriceInput = ProrateInput & {
  nights: number;
  value: string;
};

export const ASIA_ONLY_LOS = 3;

export const newPackageOptionId = (): PackageOptionId => uuid();

export const SELL_ADJUSTMENT_MODES = ['none', 'fixed', 'daily', 'pct'] as const;

export type SellAdjustmentMode = (typeof SELL_ADJUSTMENT_MODES)[number];

// Extra nights are sold at a slight discount to the per-night rate the LOS implies.
export const EXTRA_NIGHT_NET_FACTOR = 0.98;

export type SellAdjustment = {
  mode: SellAdjustmentMode;
  amount: number;
  nights?: number;
};

export function adjustSellKeepingMargin(sell: number, net: number, adjustment: SellAdjustment) {
  const { mode, amount, nights = 1 } = adjustment;
  if (mode === 'none' || amount === 0 || sell <= 0) {
    return { sell, net };
  }

  const marginFraction = (sell - net) / sell;
  const adjusted =
    mode === 'fixed' ? sell + amount : mode === 'daily' ? sell + amount * nights : sell * (1 + amount / 100);
  const newSell = adjusted > 0 ? Math.round(adjusted) : sell;
  return { sell: newSell, net: newSell * (1 - marginFraction) };
}

export function extraNightSellFromLos(sell: number, nights: number): string {
  if (!(sell > 0) || !(nights > 0)) {
    return '';
  }
  const perNight = Math.round(sell / nights / 10) * 10;
  return perNight > 0 ? String(perNight) : '';
}

// netFraction (FX already applied), when given, overrides the room's own sell/net margin.
export function extraNightFromNewBaseLos(
  newBaseSell: number,
  newBaseNet: number,
  baseNights: number,
  netFraction?: number,
) {
  const sell = Number(extraNightSellFromLos(newBaseSell, baseNights)) || 0;
  if (sell <= 0) {
    return { sell: 0, net: 0 };
  }
  if (netFraction !== undefined) {
    return { sell, net: Number(formulaExtraNightNet(String(sell), netFraction)) };
  }
  const marginFraction = newBaseSell > 0 ? (newBaseSell - newBaseNet) / newBaseSell : 0;
  return { sell, net: floatify(sell * (1 - marginFraction) * EXTRA_NIGHT_NET_FACTOR) };
}

// Extra nights anchor on the shortest LOS; a user-pinned sell is never re-derived.
export function deriveExtraNightSell(
  pricing: Pick<RoomPricing, 'sellByLos' | 'extraNightSell' | 'extraNightSellEdited'>,
  lengthsOfStay: Array<number>,
): string {
  if (pricing.extraNightSellEdited) {
    return pricing.extraNightSell;
  }
  const [baseLos] = lengthsOfStay;
  return extraNightSellFromLos(Number(pricing.sellByLos[baseLos]) || 0, baseLos);
}

export function prorateSellByLos(
  current: Pick<RoomPricing, 'sellByLos' | 'sellEditedByLos'>,
  { lengthsOfStay, isAsiaOnly }: ProrateInput,
): Pick<RoomPricing, 'sellByLos' | 'sellEditedByLos'> {
  const sortedLos = [...lengthsOfStay].sort((a, b) => a - b);
  const baseLos = sortedLos.find((los) => Number(current.sellByLos[los]) > 0);
  if (baseLos === undefined) {
    return { sellByLos: current.sellByLos, sellEditedByLos: current.sellEditedByLos };
  }

  const basePrice = Number(current.sellByLos[baseLos]);
  const sellByLos = { ...current.sellByLos };
  for (const los of sortedLos) {
    if (los === baseLos || current.sellEditedByLos[los] || (isAsiaOnly && los !== ASIA_ONLY_LOS)) {
      continue;
    }
    sellByLos[los] = String(Math.round((basePrice / baseLos) * los));
  }

  return { sellByLos, sellEditedByLos: current.sellEditedByLos };
}

export function netAtAnchorMargin(
  pricing: Pick<RoomPricing, 'sellByLos' | 'netByLos'>,
  nights: number,
  lengthsOfStay: Array<number>,
): string | undefined {
  const sell = Number(pricing.sellByLos[nights]) || 0;
  if (sell <= 0 || (pricing.netByLos[nights] ?? '').trim() !== '') {
    return undefined;
  }
  const anchor = [...lengthsOfStay]
    .sort((a, b) => a - b)
    .find((los) => los !== nights && Number(pricing.sellByLos[los]) > 0 && Number(pricing.netByLos[los]) > 0);
  if (anchor === undefined) {
    return undefined;
  }
  return String(Math.round((sell * Number(pricing.netByLos[anchor])) / Number(pricing.sellByLos[anchor])));
}

export function setSellPrice(
  current: Pick<RoomPricing, 'sellByLos' | 'sellEditedByLos'>,
  { nights, value, lengthsOfStay, isAsiaOnly }: SetSellPriceInput,
): Pick<RoomPricing, 'sellByLos' | 'sellEditedByLos'> {
  const edited = {
    sellByLos: { ...current.sellByLos, [nights]: value },
    sellEditedByLos: { ...current.sellEditedByLos, [nights]: value.trim() !== '' },
  };

  if (nights !== Math.min(...lengthsOfStay)) {
    return edited;
  }

  const prorated = prorateSellByLos(
    { sellByLos: edited.sellByLos, sellEditedByLos: { ...edited.sellEditedByLos, [nights]: false } },
    { isAsiaOnly, lengthsOfStay },
  );

  return { sellByLos: prorated.sellByLos, sellEditedByLos: edited.sellEditedByLos };
}

interface NetFormula {
  applyToExtraNight: boolean;
  lengthsOfStay: Array<number>;
  netPercent: number;
  audToNetRate: number;
}

function formulaExtraNightNet(extraNightSell: string, netFraction: number): string {
  return String(floatify((Number(extraNightSell) || 0) * netFraction));
}

function applyNetFormulaToRoom(
  pricing: RoomPricing,
  { applyToExtraNight, lengthsOfStay, netPercent, audToNetRate }: NetFormula,
): RoomPricing {
  const netFraction = (netPercent / 100) * audToNetRate;
  const netByLos = { ...pricing.netByLos };
  for (const nights of lengthsOfStay) {
    netByLos[nights] = String(floatify((Number(pricing.sellByLos[nights]) || 0) * netFraction));
  }
  return {
    ...pricing,
    netByLos,
    netPercent,
    extraNightNet: applyToExtraNight
      ? formulaExtraNightNet(pricing.extraNightSell, netFraction)
      : pricing.extraNightNet,
  };
}

export function applyNetFormulaToPricing(
  pricingByRoomId: Record<PackageOptionId, RoomPricing>,
  { roomIds, ...formula }: NetFormula & { roomIds: Array<PackageOptionId> },
): Record<PackageOptionId, RoomPricing> {
  const updated = { ...pricingByRoomId };
  for (const id of roomIds) {
    if (updated[id]) {
      updated[id] = applyNetFormulaToRoom(updated[id], formula);
    }
  }
  return updated;
}

export function isRoomInNetFormulaScope(pricing: RoomPricing | undefined): boolean {
  return pricing?.netPercent != null && pricing.netPercent > 0;
}

export function reapplyNetFormula(
  pricingByRoomId: Record<PackageOptionId, RoomPricing>,
  lengthsOfStay: Array<number>,
  { fromRate, toRate }: { fromRate: number; toRate: number },
): Record<PackageOptionId, RoomPricing> {
  let next: Record<PackageOptionId, RoomPricing> | undefined;
  for (const [id, pricing] of Object.entries(pricingByRoomId)) {
    if (!isRoomInNetFormulaScope(pricing)) {
      continue;
    }
    const netPercent = pricing.netPercent ?? 0;
    const previousExtraNightNet = formulaExtraNightNet(pricing.extraNightSell, (netPercent / 100) * fromRate);
    next ??= { ...pricingByRoomId };
    next[id] = applyNetFormulaToRoom(pricing, {
      applyToExtraNight: pricing.extraNightNet === previousExtraNightNet,
      lengthsOfStay,
      netPercent,
      audToNetRate: toRate,
    });
  }
  return next ?? pricingByRoomId;
}

export function clearNonAsiaOnlyPricing(pricing: RoomPricing): RoomPricing {
  const keepAsiaOnlyLos = <T>(byLos: Record<number, T>) =>
    Object.fromEntries(Object.entries(byLos).filter(([los]) => Number(los) === ASIA_ONLY_LOS));

  return {
    ...pricing,
    sellByLos: keepAsiaOnlyLos(pricing.sellByLos),
    sellEditedByLos: keepAsiaOnlyLos(pricing.sellEditedByLos),
    netByLos: keepAsiaOnlyLos(pricing.netByLos),
  };
}

export function defaultRoomPricing(occupancy: string): RoomPricing {
  return {
    allocation: '',
    occupancy,
    sellByLos: {},
    sellEditedByLos: {},
    netByLos: {},
    extraNightSell: '',
    extraNightSellEdited: false,
    extraNightNet: '',
    otaPrice: '',
  };
}

export const DEFAULT_NET_RATE_FORMULA_STATE: NetRateFormulaState = {
  netPercent: '0',
  applyToExtraNight: true,
  scope: 'all',
  selectedRoomIds: [],
};

export const DEFAULT_OPTION_STATE: OptionState = {
  lengthsOfStay: [3],
  roomsByCategory: [[{ id: newPackageOptionId(), assetId: '', assetName: '', asiaOnly: false }], [], [], []],
  selectedLos: 3,
  pricingByRoomId: {},
  netRateFormula: DEFAULT_NET_RATE_FORMULA_STATE,
};

export function collectRoomAssets(
  accountAssets: Array<OpportunityRoomAsset> = [],
  created: Array<CreatedAsset> = [],
): Array<RoomAssetLink> {
  const fromAccount = accountAssets
    .filter((asset) => !!asset.name)
    // Rebuilt on every render, so the fallback id must be deterministic, not a fresh UUID.
    // Names are not unique, so fall back to the row id before the name.
    .map((asset) => ({
      id: asset.sfid ?? (asset.id != null ? `id:${asset.id}` : `name:${asset.name}`),
      sfid: asset.sfid ?? undefined,
      name: asset.name,
    }));

  return uniqueBy([...fromAccount, ...created], (asset) => asset.id);
}

// Two base rows on one asset make the forecast read the same packages cell twice and double every total.
export function takenRoomAssetKeys(basicRooms: Array<CategoryRoom>, keepRoomId?: PackageOptionId): Set<string> {
  return new Set(basicRooms.filter((room) => room.assetId && room.id !== keepRoomId).map((room) => room.assetId));
}

// A package with no room asset picked is left out of the saved draft rather than blocking the save.
export function withoutUnlinkedPackages(optionStates: Record<number, OptionState>): Record<number, OptionState> {
  return Object.fromEntries(
    Object.entries(optionStates).map(([option, state]) => {
      const roomsByCategory = state.roomsByCategory.map((rooms) => rooms.filter((room) => room.assetId));
      const keptIds = new Set(roomsByCategory.flat().map((room) => room.id));
      const pricingByRoomId = Object.fromEntries(
        Object.entries(state.pricingByRoomId).filter(([id]) => keptIds.has(id)),
      );
      return [option, { ...state, roomsByCategory, pricingByRoomId }];
    }),
  );
}

export function mergeSelectionById<T extends { id: string }>(
  ids: Array<string>,
  selectedById: Record<string, T>,
  visibleRows: Array<T>,
): Record<string, T> {
  const visibleById = new Map(visibleRows.map((row) => [row.id, row]));
  const merged: Record<string, T> = {};
  for (const id of ids) {
    const row = selectedById[id] ?? visibleById.get(id);
    if (row) {
      merged[id] = row;
    }
  }
  return merged;
}
